import {useTranslation} from 'react-i18next';
import {
    getHealthDeclarationId,
    getMinMaxAge,
    minMaxPeopleForType
} from 'src/components/clinic/components/ClinicForm/clinicFormUtils';
import {MinusIcon} from 'src/components/icons/MinusIcon';
import {PlusIcon} from 'src/components/icons/PlusIcon';
import {BookingActions, useBookingContext} from 'src/context/BookingContext';
import {getTextFromLocale} from 'src/services/i18n/locale';
import './LocationForm.scss';
import {AppointmentCategory, AppointmentType, ReasonFilterObject, ScheduledStop} from 'src/services/api/types/types';
import {DataActions, useLocationDetailsContext} from 'src/context/LocationDetailsContext';
import {useEffect, useState} from 'react';
import {VisitCategory} from 'src/components/main/components/PickVisitCategory';
import {Button} from 'src/components/form/button/Button';
import {ScheduledStops} from 'src/components/main/components/ScheduledStops';

export function LocationForm() {
    const {t} = useTranslation();

    const {
        bookingState: {bookingType, reasonState},
        dispatch
    } = useBookingContext();

    const {
        locationDataState: {
            loading,
            location,
            locationStops,
            appointmentCategories,
            visitReasons,
            appointmentTypeIds,
            hasBooking,
            hasDropin
        },
        locationDispatch
    } = useLocationDetailsContext();

    const {reason, appointmentType, numberPatients} = reasonState;

    const selectedReasonId = reason?.id ? parseInt(reason.id) : -1;
    const selectedAppointmentTypeId = appointmentType?.id ? appointmentType.id : -1;

    const [visitReasonsGroup, setVisitReasonsGroup] = useState<Record<VisitCategory, Array<ReasonFilterObject>>>(
        {} as Record<VisitCategory, Array<ReasonFilterObject>>
    );
    const [uniqueAppointmentCategories, setUniqueAppointmentCategories] = useState<Array<AppointmentCategory>>([]);
    const [appointmentCategoriesFiltered, setAppointmentCategoriesFiltered] = useState<Array<AppointmentCategory>>([]);
    const [appointmentTypes, setAppointmentTypes] = useState<Array<AppointmentType>>([]);

    const appointmentTypesFlat = uniqueAppointmentCategories
        .flatMap(({appointmentTypes}) => appointmentTypes)
        .filter(({visitReasonIds}) => visitReasonIds.includes(selectedReasonId));

    const [minPeople, maxPeople] = minMaxPeopleForType(appointmentTypesFlat, selectedReasonId);

    const numberOfPeople = numberPatients || minPeople;

    const scheduleWithStops: ScheduledStop[] = Object.values(
        locationStops.reduce(
            (acc, stop) => {
                const date = stop.from.substring(0, 10);

                const key = [
                    date,
                    stop.from.substring(11),
                    stop.to.substring(11),
                    stop.information ?? '',
                    stop.status
                ].join('|');

                if (!acc[key]) {
                    acc[key] = {
                        locationId: stop.locationId,
                        clinicId: stop.clinicId,
                        careGiver: stop.careGiver,
                        from: stop.from,
                        to: stop.to,
                        information: stop.information,
                        status: stop.status,
                        visitReasons: {}
                    };
                }

                Object.entries(stop.visitReasons ?? {}).forEach(([locale, reasons]) => {
                    if (!acc[key].visitReasons![locale]) {
                        acc[key].visitReasons![locale] = [];
                    }

                    reasons.forEach((reason) => {
                        const exists = acc[key].visitReasons![locale].some((r) => r.id === reason.id);

                        if (!exists) {
                            acc[key].visitReasons![locale].push(reason);
                        }
                    });
                });

                return acc;
            },
            {} as Record<string, ScheduledStop>
        )
    );

    const appointmentCategoryGroups = appointmentCategoriesFiltered
        .filter(({appointmentTypes}) => {
            return appointmentTypes.some(({visitReasonIds}) => visitReasonIds.includes(selectedReasonId));
        })
        .map((cat) => {
            return {
                ...cat,
                appointmentTypes: cat.appointmentTypes.filter(({patients, eidSelfOnly, visitReasonIds}) => {
                    // Safety belt, remove any self only bookable types with >1 patients
                    if (eidSelfOnly && numberOfPeople > 1) {
                        return false;
                    }

                    return visitReasonIds.includes(selectedReasonId) && patients === numberOfPeople;
                })
            };
        });

    const nextDisabled = bookingType === 'dropIn' ? false : selectedReasonId === -1 || selectedAppointmentTypeId === -1;

    useEffect(() => {
        if (!loading && location?.id) {
            const loadSchedule = async () => {
                const uniqueAppointmentCategories = appointmentCategories.flat().filter((value, index, self) => {
                    return self.findIndex((v) => v.name === value.name) === index;
                });

                const appointmentCategoriesFull = appointmentCategories
                    .flat()
                    .reduce<Array<AppointmentCategory>>((categories, category) => {
                        const existing = categories.find(({name}) => name === category.name);

                        if (!existing) {
                            categories.push({...category, appointmentTypes: [...category.appointmentTypes]});
                            return categories;
                        }

                        const typesById = new Map(existing.appointmentTypes.map((type) => [type.id, type]));

                        category.appointmentTypes.forEach((type) => {
                            typesById.set(type.id, type);
                        });

                        existing.appointmentTypes = Array.from(typesById.values());
                        return categories;
                    }, []);

                // Find unique visitReasons under uniqueAppointmentCategories
                const uniqueVisitReasonIds = Array.from(
                    new Set(
                        appointmentCategoriesFull.flatMap((category) =>
                            category.appointmentTypes.flatMap((type) => type.visitReasonIds)
                        )
                    )
                );

                //Filter out corresponding visit reason to get visit reasons names
                const selectVisitReasons = visitReasons.filter((reason) =>
                    uniqueVisitReasonIds.includes(parseInt(reason.id, 10))
                );

                const visitReasonsGroups = selectVisitReasons.reduce(
                    (acc, reason) => {
                        if (uniqueVisitReasonIds.includes(parseInt(reason.id))) {
                            const {category} = reason;
                            acc[category] = acc[category] ? acc[category] : [];
                            acc[category].push(reason);
                        }

                        return acc;
                    },
                    {} as Record<VisitCategory, Array<ReasonFilterObject>>
                );

                const appointmentCategoriesFiltered = appointmentCategories.flat().filter((value, index, self) => {
                    return self.findIndex((v) => v.name === value.name) === index;
                });

                const appointmentTypesFlat = appointmentCategoriesFull.flatMap(({appointmentTypes}) =>
                    appointmentTypes.filter(({patients, eidSelfOnly}) => {
                        if (eidSelfOnly) {
                            return patients === 1;
                        }
                        return true;
                    })
                );

                setVisitReasonsGroup(visitReasonsGroups);
                setUniqueAppointmentCategories(uniqueAppointmentCategories);
                setAppointmentCategoriesFiltered(appointmentCategoriesFiltered);
                setAppointmentTypes(appointmentTypesFlat);
            };

            loadSchedule();
        }
    }, [location, appointmentTypeIds, loading, visitReasons]);

    const onReasonChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const clickedReason = parseInt(e.target.value, 10);
        const matchingTypesFlat = appointmentCategoriesFiltered
            .flatMap(({appointmentTypes}) =>
                appointmentTypes.filter(({patients, eidSelfOnly}) => {
                    if (eidSelfOnly) {
                        return patients === 1;
                    }
                    return true;
                })
            )
            .filter(({visitReasonIds}) => visitReasonIds.includes(clickedReason || -1));

        const [minPeople] = minMaxPeopleForType(matchingTypesFlat);

        let matchingTypes = matchingTypesFlat.filter(({id}) => id === selectedAppointmentTypeId);
        if (!matchingTypes.length) {
            matchingTypes = matchingTypesFlat.filter(({patients}) => patients === numberPatients);
        }
        if (!matchingTypes.length) {
            matchingTypes = matchingTypesFlat.filter(({patients}) => patients === minPeople);
        }

        const reason = visitReasons.find(({id}) => parseInt(id, 10) === clickedReason) as ReasonFilterObject;

        if (matchingTypes.length === 1) {
            const matchingType = matchingTypes[0];
            dispatch({
                type: BookingActions.SET_REASON_DATA,
                value: {
                    reason: reason,
                    numberPatients: matchingType.patients,
                    appointmentType: matchingType,
                    healthDeclarationDefinitionId: getHealthDeclarationId(
                        appointmentCategoriesFiltered,
                        matchingType.id
                    ),
                    allowedAge: getMinMaxAge(appointmentCategoriesFiltered, matchingType.id)
                }
            });
        } else {
            const minOrSameNumPeople =
                matchingTypesFlat.find(({patients}) => patients === numberOfPeople)?.patients || minPeople;
            dispatch({
                type: BookingActions.SET_REASON_DATA,
                value: {
                    reason: reason,
                    numberPatients: minOrSameNumPeople,
                    appointmentType: null,
                    healthDeclarationDefinitionId: null,
                    allowedAge: null
                }
            });
        }
    };

    const onNumberPeopleClick = (amount: number) => {
        const newNumber = numberOfPeople + amount;

        const matchingTypes = appointmentCategoriesFiltered
            .flatMap(({appointmentTypes}) =>
                appointmentTypes.filter(({patients, eidSelfOnly}) => {
                    if (eidSelfOnly) {
                        return patients === 1;
                    }
                    return true;
                })
            )
            .filter(
                ({visitReasonIds, patients}) =>
                    visitReasonIds.includes(selectedReasonId || -1) && patients === newNumber
            );

        if (matchingTypes.length === 1) {
            const matchingType = matchingTypes[0];
            dispatch({
                type: BookingActions.SET_REASON_DATA,
                value: {
                    ...reasonState,
                    numberPatients: newNumber,
                    appointmentType: matchingType,
                    healthDeclarationDefinitionId: getHealthDeclarationId(
                        appointmentCategoriesFiltered,
                        matchingType.id
                    )
                }
            });
        } else {
            dispatch({
                type: BookingActions.SET_REASON_DATA,
                value: {
                    ...reasonState,
                    numberPatients: newNumber,
                    appointmentType: null,
                    healthDeclarationDefinitionId: null
                }
            });
        }
    };

    const selectType = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const matchingType = appointmentTypes.find(({id}) => id === parseInt(e.target.value, 10)) as AppointmentType;

        dispatch({
            type: BookingActions.SET_REASON_DATA,
            value: {
                ...reasonState,
                appointmentType: matchingType,
                healthDeclarationDefinitionId: getHealthDeclarationId(appointmentCategoriesFiltered, matchingType.id),
                allowedAge: getMinMaxAge(appointmentCategoriesFiltered, matchingType.id)
            }
        });
    };

    const isNumPeopleDisabled = (maxNumber: number) => {
        return selectedReasonId === -1 || numberOfPeople === maxNumber;
    };

    return (
        <>
            <form className='location-form'>
                {bookingType === 'dropIn' && hasDropin && (
                    <>
                        <p className='dropin-info'>{t('clinic_form.drop_in_info')}</p>
                        {/* {clinic?.eidDropinSelfOnly && (
                        <p className='dropin-info'>
                            <b>{t('clinic_form.drop_in_self_only_info')}</b>
                        </p>
                    )} */}
                    </>
                )}
                {bookingType === 'booking' && hasBooking && (
                    <>
                        <div aria-live='polite' aria-atomic='true' className='screen-reader-only'>
                            {selectedReasonId === -1 && <p>{t('clinic_form.screen_reader_fill_in_form')}</p>}
                            {selectedReasonId !== -1 && (
                                <p>
                                    {t('clinic_form.screen_reader_reason', {
                                        visitReason: getTextFromLocale(reason?.name)
                                    })}
                                    {t('clinic_form.screen_reader_people_count', {numberOfPeople})}
                                    {appointmentType !== null && (
                                        <>
                                            {t('clinic_form.screen_reader_visit_type', {
                                                visitType: appointmentType.name
                                            })}
                                        </>
                                    )}
                                </p>
                            )}
                        </div>
                        <span className='select-container'>
                            {selectedReasonId !== -1 && (
                                <label htmlFor='reason' className='select-with-label'>
                                    {t('clinic_form.type_of_vaccine')}
                                </label>
                            )}
                            <select
                                id='reason'
                                value={selectedReasonId}
                                className={`select ${selectedReasonId !== -1 ? 'has-selection' : ''}`}
                                onChange={(e) => onReasonChange(e)}
                            >
                                <option disabled value={-1}>
                                    {t('clinic_form.type_of_vaccine')}
                                </option>
                                {Object.keys(visitReasonsGroup).map((key) => {
                                    return (
                                        <optgroup key={key} label={t(`reason_group.${key as VisitCategory}`)}>
                                            {visitReasonsGroup[key as VisitCategory].map((reason) => {
                                                return (
                                                    <option key={reason.id} value={reason.id}>
                                                        {reason.id === `${selectedReasonId}`
                                                            ? `${t(`reason_group.${reason.category}`)}  - `
                                                            : ''}{' '}
                                                        {getTextFromLocale(reason?.name)}
                                                    </option>
                                                );
                                            })}
                                        </optgroup>
                                    );
                                })}
                            </select>
                        </span>
                        <div className='amount-input'>
                            <label className='amount-input__label'>{t('clinic_form.people_count_vaccine')}</label>
                            <div className='amount-input__label__content'>
                                <button
                                    disabled={isNumPeopleDisabled(minPeople)}
                                    className='amount-button'
                                    type='button'
                                    onClick={() => onNumberPeopleClick(-1)}
                                    aria-label='minus'
                                >
                                    <MinusIcon />
                                </button>
                                <span className='amount-input__label__content__input'>{numberOfPeople}</span>
                                <button
                                    disabled={isNumPeopleDisabled(maxPeople)}
                                    className='amount-button'
                                    type='button'
                                    onClick={() => onNumberPeopleClick(1)}
                                    aria-label='plus'
                                >
                                    <PlusIcon />
                                </button>
                            </div>
                        </div>
                        {numberOfPeople > 1 && (
                            <p className='amount-input__info'>{t('clinic_form.many_people_info')}</p>
                        )}
                        <span className='select-container'>
                            {selectedAppointmentTypeId !== -1 && (
                                <label htmlFor='visit-type' className='select-with-label'>
                                    {t('clinic_form.type_of_visit')}
                                </label>
                            )}
                            <select
                                disabled={selectedReasonId === -1}
                                id='visit-type'
                                value={selectedAppointmentTypeId}
                                className={`select${selectedAppointmentTypeId !== -1 ? ' has-selection' : ''}`}
                                onChange={(e) => selectType(e)}
                            >
                                <option disabled value={-1}>
                                    {t('clinic_form.type_of_visit')}
                                </option>
                                {appointmentCategoryGroups.map((cat) => {
                                    if (cat.appointmentTypes.length === 0) {
                                        const maxPatients = Math.max(
                                            ...(uniqueAppointmentCategories
                                                .find(({id}) => id === cat.id)
                                                ?.appointmentTypes.map(({patients}) => patients) || [])
                                        );
                                        return (
                                            <optgroup key={cat.id} label={cat.name}>
                                                <option disabled key={`${cat.id}-0`} value='-1'>
                                                    {t('clinic_form.max_people_needed', {count: maxPatients})}
                                                </option>
                                            </optgroup>
                                        );
                                    }

                                    return (
                                        <optgroup key={cat.id} label={cat.name}>
                                            {cat.appointmentTypes.map((type) => {
                                                return (
                                                    <option key={`${cat.id}-${type.id}`} value={type.id}>
                                                        {type.name}
                                                    </option>
                                                );
                                            })}
                                        </optgroup>
                                    );
                                })}
                            </select>
                        </span>
                        {appointmentType?.eidSelfOnly === true && (
                            <p className='select-container__info'>{t('clinic_form.self_only_info')}</p>
                        )}
                    </>
                )}

                {(hasBooking || hasDropin) && (
                    <Button
                        id='booking-start'
                        onClick={() => {
                            if (bookingType === 'dropIn') {
                                // TODO: Probably add specific Action for clearing reason data
                                dispatch({
                                    type: BookingActions.SET_REASON_DATA,
                                    value: {
                                        reason: null,
                                        numberPatients: null,
                                        appointmentType: null,
                                        healthDeclarationDefinitionId: null,
                                        allowedAge: null
                                    }
                                });
                            } else {
                                const selectedType = appointmentTypes.find(({id}) => id === selectedAppointmentTypeId);
                                if (selectedType) {
                                    const matchingAppointmentTypeIds = Array.from(
                                        new Set(
                                            appointmentTypes
                                                .filter(({name}) => name === selectedType.name)
                                                .map(({id}) => id)
                                        )
                                    );
                                    locationDispatch({
                                        type: DataActions.SET_APPOINTMENT_TYPES,
                                        value: matchingAppointmentTypeIds
                                    });
                                }
                            }

                            dispatch({type: BookingActions.NEXT_STEP});
                        }}
                        disabled={nextDisabled}
                    >
                        {t('clinic_form.next')}
                    </Button>
                )}
            </form>
            <div className='location-schedule-container'>
                <h3 id='location-schedule-header'>{t('location_popup.schedule')}</h3>
                <ScheduledStops scheduledStops={scheduleWithStops} includeInfo={true} />
            </div>
        </>
    );
}
